import { Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, timer } from 'rxjs';
import { map, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { translate } from '@jsverse/transloco';
import { ActivityProgressService } from 'src/app/services/util/activity-progress.service';
import { calculateDuration, formatActivityDateTime, toUtcString } from 'src/app/services/util/utils';
import { animate, state, style, transition, trigger } from '@angular/animations';


export interface AgoText {
  key: string;
  params: Record<string, number>;
}

export interface Stage {
  type: string;
  location: string;
  stageNumber?: number | string;
  stageName?: string;
  status?: 'pending' | 'completed' | 'failed' | 'active';
  timestamp?: Date;
  duration?: number;
  count?: {
    total?: number;
    completed?: number;
    skipped?: number;
    failed?: number;
  };
  components?: any[];
}


interface StageCountItem {
  key: 'total' | 'completed' | 'pending' | 'skipped' | 'failed';
  label: string;
  value: number;
}

type StatusKey = 'all' | 'inprogress' | 'completed' | 'failed' | 'slower';

@Component({
  selector: 'app-activity-right-panel',
  templateUrl: './activity-right-panel.component.html',
  styleUrl: './activity-right-panel.component.scss',
  standalone: false,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', opacity: 0, transform: 'translateY(-6px)' })),
      state('expanded', style({ height: '*', minHeight: '*', opacity: 1, transform: 'none' })),
      transition('collapsed <=> expanded', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class ActivityRightPanelComponent implements OnInit, OnChanges, OnDestroy {
  private destroyRef = inject(DestroyRef);
  readonly progress = inject(ActivityProgressService);

  @Input() set selectedStage(value: any) {
    const stageChanged = !this.isSameStage(this._selectedStage, value);
    this._selectedStage = value;
    this.buildTitle();
    if (value) {
      this.lastUpdated$.next(new Date());
    }
    if (stageChanged) {
      this.selectedStatus = 'all';   
    }
  }
  @Input() mainData: any;
  get selectedStage(): Stage | null {
    return this._selectedStage;
  }
  private _selectedStage: Stage | null = null;

  @Input() stageTitle = '';
  @Input() lastUpdated: Date | string | number | null = null;
  @Input() loading = false;
  @Input() downloadDisabled = false;

  @Output() refresh = new EventEmitter<void>();
  @Output() download = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  selectedStatus: StatusKey = 'all';
  private readonly componentRows = new WeakMap<object, any>();
  formatActivityDateTime = formatActivityDateTime;
  toUtcString = toUtcString;
  calculateDuration = calculateDuration;
  statusIcon: Record<string, string> = { failed: 'warning_amber', success: 'check_circle_filled' };

  typeMenuOpen = false;
  @ViewChild('typeDropdown') typeDropdown?: ElementRef<HTMLElement>;

  selectedType = 'all';
  searchText = '';

  constructor(private activityProgressService: ActivityProgressService) {}

  private readonly statusNames: Record<StatusKey, string> = {
    all: 'all',
    inprogress: 'inprogress',
    completed: 'completed',
    failed: 'failed',
    slower: 'slower than expected',
  };

  get countItems(): StageCountItem[] {
    const count = this.stage?.count ?? {};
    const components: any[] = this.stage?.components ?? [];
    const total = Number(count.total ?? components.length ?? 0);
    const completed = Number(count.completed ?? components.filter(c => c.status === 'success').length);
    const skipped = Number(count.skipped ?? 0);
    const failed = Number(count.failed ?? components.filter(c => c.status === 'failed').length);
    const pending = Math.max(total - (completed + skipped + failed), 0);
    return [
      { key: 'total',     label: 'Total',     value: total },
      { key: 'completed', label: 'Completed', value: completed },
      { key: 'pending',   label: 'Pending',   value: pending },
      { key: 'skipped',   label: 'Skipped',   value: skipped },
      { key: 'failed',    label: 'Failed',    value: failed },
    ];
  }

  get stage(): any {
    if (!this._selectedStage) {
      return null;
    }
    return (this.mainData?.stages ?? []).find(
      (s: any) => s?.type === this._selectedStage?.type && s?.location === this._selectedStage?.location
    ) || this._selectedStage;
  }

  get hasCounts(): boolean {
    return !!this.stage?.count?.total || (this.stage?.components?.length ?? 0) > 0;
  }

  private _title = '';
  clickSpinning = false;

  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  lastUpdated$ = new BehaviorSubject<Date>(new Date());

  ago$ = this.lastUpdated$.pipe(
    switchMap(lastUpdate =>
    timer(0, 1000).pipe(
      map(() => this.toAgoText(lastUpdate)),
      distinctUntilChanged((a, b) =>
        a.key === b.key && JSON.stringify(a.params) === JSON.stringify(b.params)
      )
    )
  ),
  takeUntilDestroyed(this.destroyRef)
  );

  private spinTimer: ReturnType<typeof setTimeout> | null = null;

  get isSpinning(): boolean {
    return this.loading || this.clickSpinning;
  }

  private isSameStage(a: any, b: any): boolean {
    return a?.type === b?.type && a?.location === b?.location;
  }

  ngOnInit(): void {
    this.buildTitle();
    if (this.lastUpdated) {
      this.lastUpdated$.next(new Date(this.lastUpdated));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stageTitle'] || changes['selectedStage'] || changes['mainData']) {
      this.buildTitle();
    }
    if (changes['lastUpdated'] && this.lastUpdated) {
      this.lastUpdated$.next(new Date(this.lastUpdated));
    }
  }

  ngOnDestroy(): void {
    if (this.spinTimer) {
      clearTimeout(this.spinTimer);
    }
  }

  onRefresh(): void {
    if (this.isSpinning) {
      return;
    }
    this.clickSpinning = true;
    this.spinTimer = setTimeout(() => (this.clickSpinning = false), 1000);
    this.lastUpdated$.next(new Date());
    this.refresh.emit();
  }

  onDownload(): void {
    if (!this.downloadDisabled) {
      this.download.emit();
    }
  }

  onClose(): void {
    this.closed.emit();
  }

  private toAgoText(date: Date): AgoText {
  const rawSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const sec = Math.floor(rawSec / 5) * 5;
    if (sec < 5) {
      return { key: 'ACTIVITY_PANEL.JUST_NOW', params: {} };
    }
    if (sec < 60) {
      return { key: 'ACTIVITY_PANEL.SECS_AGO', params: { count: sec } };
    }

    const min = Math.floor(sec / 60);
    const remSec = sec % 60;

    if (min < 60) {
      if (remSec === 0) {
        return {
          key: min === 1 ? 'ACTIVITY_PANEL.MIN_AGO' : 'ACTIVITY_PANEL.MINS_AGO',
          params: { count: min },
        };
      }
      return { key: 'ACTIVITY_PANEL.MIN_SECS_AGO', params: { min, sec: remSec } };
    }

    const hr = Math.floor(min / 60);
    return {
      key: hr === 1 ? 'ACTIVITY_PANEL.HR_AGO' : 'ACTIVITY_PANEL.HRS_AGO',
      params: { count: hr },
    };
}

  private buildTitle(): void {
      this.title = this.stageTitle;
  }

  get componentTypeOptions(): { value: string; label: string }[] {
  const components: any[] = this.stage?.components ?? [];

  const types: string[] = Array.from(
    new Set<string>(
      components
        .map(c => c?.componentType)
        .filter((t): t is string => !!t)
    )
  ).sort();

  return [
    { value: 'all', label: 'All Types' },
    ...types.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) })),
  ];
}

onSearchChange(event: Event): void {
  this.searchText = (event.target as HTMLInputElement).value;
}

clearSearch(): void {
  this.searchText = '';
}

toggleTypeMenu(): void {
  this.typeMenuOpen = !this.typeMenuOpen;
}

selectType(value: string): void {
  this.selectedType = value;
  this.typeMenuOpen = false;
}

@HostListener('document:click', ['$event.target'])
onDocumentClick(target: EventTarget | null): void {
  if (this.typeMenuOpen && !this.typeDropdown?.nativeElement.contains(target as Node)) {
    this.typeMenuOpen = false;
  }
}

@HostListener('document:keydown.escape')
onEscape(): void {
  this.typeMenuOpen = false;
}

get selectedTypeLabel(): string {
  if (!this.selectedType || this.selectedType === 'all') {
    return 'All Types';
  }
  return this.selectedType.charAt(0).toUpperCase() + this.selectedType.slice(1);
}

private isOverExpected(component: any): boolean {
  const expected = Number(component.expectedDurationMs);
  return component.status === 'inprogress' && expected > 0 && this.getElapsedMs(component) >= expected;
}

  /** Components with `thresholdReached` computed the same way as the other page */
  get allComponents(): any[] {
    const now = Date.now();
    return (this.stage?.components ?? []).map((comp: any) => {
      const row = this.componentRows.get(comp) ?? { ...comp, arrowClick: false };
      this.componentRows.set(comp, row);
      row.thresholdReached = this.isOverExpected(comp);
      row.performance = this.performanceOf(comp) ?? '';
      return row;
    });
  }

  private normalizeUtc(dateStr: string): string {
    return dateStr?.endsWith('Z') ? dateStr : dateStr + 'Z';
  }

  getExpectedLabel(component: any): string {
    return this.progress.formatDuration(Number(component.expectedDurationMs));
  }

  private getElapsedMs(component: any): number {
    return component.status === 'inprogress'
      ? Date.now() - Date.parse(component.start)
      : Number(component.health?.elapsedDurationMs);
  }

  getDeviationPercent(component: any): number {
    const expected = Number(component.expectedDurationMs);
    if (!expected) return 0;
    const diff = expected - this.getElapsedMs(component);
    return Math.round((diff / expected) * 100);
  }


  getProgressPercent(component: any): number {
    const expected = Number(component.expectedDurationMs);
    if (!expected) return 0;
    const ratio = this.getElapsedMs(component) / expected;
    return Math.max(0, Math.min(Math.round(ratio * 80), 100));
  }

  getProgressLabel(component: any): string {
  if (component.status === 'success') {
    const deviation = this.getDeviationPercent(component);
    if (deviation > 0) return `Completed · ${deviation}% faster`;
    if (deviation < 0) return 'Completed · Slower than expected';
    return 'Completed · On time';
  }

  if (component.status === 'failed') return 'Failed';

  if (this.isOverExpected(component)) return 'InProgress · Threshold Reached';
  return 'InProgress';
}

  isSlower(component: any): boolean {
    return this.getDeviationPercent(component) < 0;
  }

  trackRow(component: any, index: number): string {
    return `${this.stage?.startTime}|${index}|${component.name}|${component.status}`;
  }

  getBarState(component: any): string {
  if (component.status === 'failed') return 'failed';

  if (component.status === 'success') {
    const deviation = this.getDeviationPercent(component);
    if (deviation > 0) return 'faster';
    if (deviation < 0) return 'slower';
    return 'on-time';
  }

  if (this.isOverExpected(component)) return 'threshold';
  return 'inprogress';
}

  get searchedComponents(): any[] {
    const term = this.searchText.trim().toLowerCase();
    return this.allComponents.filter(c =>
      (this.selectedType === 'all' || c.componentType === this.selectedType) &&
      (!term || (c.name ?? '').toLowerCase().includes(term))
    );
  }

  private readonly statusFilters: Record<StatusKey, (c: any) => boolean> = {
    all:        () => true,
    inprogress: c => c.status === 'inprogress',
    completed:  c => c.status === 'success',
    failed:     c => c.status === 'failed',
    slower:     c => c.status === 'success' && c.performance === 'slow',
  };

  get filterColumns(): { key: StatusKey; label: string; count: number }[] {
    const comps = this.searchedComponents;
    const defs: { key: StatusKey; label: string }[] = [
      { key: 'all',        label: translate('ABOUT.ALL') },
      { key: 'completed',  label: translate('ABOUT.COMPLETED') },
      { key: 'inprogress', label: translate('ABOUT.INPROGRESS') },
      { key: 'failed',     label: translate('ABOUT.FAILED') },
      { key: 'slower',  label: translate('ABOUT.SLOWER_THAN_EXPECTED') }, 
    ];
    return defs.map(d => ({ ...d, count: comps.filter(this.statusFilters[d.key]).length }));
  }

  get filteredComponents(): any[] {
    return this.searchedComponents.filter(this.statusFilters[this.selectedStatus]);
  }
  
  onTypeChange(event: Event): void {
    this.selectedType = (event.target as HTMLSelectElement).value;
  }

  selectStatus(key: StatusKey): void {
    this.selectedStatus = key;
  }

  get emptyMessage(): string {
    return this.selectedStatus === 'all'
      ? translate('ABOUT.EMPTY_COMPONENTS_STATUS')
      : translate('ABOUT.EMPTY_COMPONENTS_STATUS', { status: this.statusNames[this.selectedStatus] });
  }

  onExpandArrowClick(item: any): void {
    const willOpen = !item.arrowClick;
    this.allComponents.forEach(c => (c.arrowClick = false));
    item.arrowClick = willOpen;
  }

  /** Classifies a component as fast, slow or ontime against its expected duration; null when not yet decidable */
  performanceOf(component: any): 'fast' | 'slow' | 'ontime' | null {
    const expectedMs = Number(component?.expectedDurationMs);
    if (!component?.start || !(expectedMs > 0)) {
      return null;
    }
    if (component.status === 'inprogress') {
      return this.activityProgressService.isThresholdReached(this.normalizeUtc(component.start), expectedMs) ? 'slow' : null;
    }
    if (component.status !== 'success' || !component.end) {
      return null;
    }
    const durationMs = this.durationMsOf(component);
    if (durationMs > expectedMs) {
      return 'slow';
    }
    if (durationMs < expectedMs) {
      return 'fast';
    }
    return 'ontime';
  }

  /** Bracketed performance label appended after the duration; empty when the component has no result */
  performanceText(component: any): string {
    const performance: string = component.performance;
    if (performance === 'ontime') {
      return `(${translate('ACTIVITY_DETAILS.ON_TIME')})`;
    }
    if (performance === 'slow') {
      return component.status === 'inprogress' ? `(${translate('ACTIVITY_DETAILS.THRESHOLD_REACHED')})` : `(${translate('ACTIVITY_DETAILS.SLOWER')})`;
    }
    if (performance === 'fast') {
      const expectedMs = Number(component.expectedDurationMs);
      const endMs = new Date(this.normalizeUtc(component.end)).getTime();
      const elapsedPercent = this.activityProgressService.getElapsedPercent(this.normalizeUtc(component.start), expectedMs, endMs);
      return `(${translate('ACTIVITY_DETAILS.FASTER', { percent: 100 - elapsedPercent })})`;
    }
    return '';
  }

  /** Elapsed milliseconds from start to end, or to now while the component is still running */
  private durationMsOf(component: any): number {
    const startMs = new Date(this.normalizeUtc(component.start)).getTime();
    const endMs = component.end ? new Date(this.normalizeUtc(component.end)).getTime() : Date.now();
    return endMs - startMs;
  }

  /** Warning limit shown as ratio and threshold duration, falling back to whichever value is present */
  warningLimitText(component: any): string {
    const ratio = Number(component?.health?.thresholdRatio);
    const expectedMs = Number(component?.expectedDurationMs);
    const hasRatio = ratio > 0;
    const hasExpected = expectedMs > 0;
    if (hasRatio && hasExpected) {
      return `${ratio}x (${this.activityProgressService.formatDuration(expectedMs)})`;
    }
    if (hasRatio) {
      return `${ratio}x`;
    }
    if (hasExpected) {
      return this.activityProgressService.formatDuration(expectedMs);
    }
    return '--';
  }

  get hasActiveFilters(): boolean {
  return this.selectedStatus !== 'all'
    || this.selectedType !== 'all'
    || this.searchText.trim().length > 0;
  }
}